/**
 * The deterministic fit of an embedded map (every stand map and report map).
 *
 * One target per map: the explicit fit target (`presentation.fitTo`) when the
 * slot names one, otherwise the property. It is applied in the frame the
 * map's container first has a size (the ResizeObserver's first delivery) and
 * again whenever the container resizes, until the owner first touches the map
 * or its controls; from then on their view wins. A new target identity fits
 * again. There are no timers: nothing guesses when the layout has settled.
 *
 * The fit frames the target in the area the registered occlusions leave free
 * (`shared/ui/mapOcclusion.ts`) and never deeper than the basemap's native
 * tile coverage.
 */
import { geoJSON, LatLngBounds, type Map as LeafletMap } from 'leaflet';
import type * as GeoJSONTypes from 'geojson';
import { clampFitZoom } from '@/shared/ui/basemapFitZoom';
import { mapFitPadding } from '@/shared/ui/mapOcclusion';
import { featureStandId } from '@/utils/featureStandId';

export interface MapFitTarget {
  /** A new key is a new target and fits again; the same key never refits on its own. */
  key: string;
  bounds: LatLngBounds;
  /** Deepest zoom the fit may reach; the mounted basemap's native coverage when absent. */
  maxZoom?: number;
}

/** Each side of a lone framed feature grows by its own span, and at least this much. */
const LONE_FEATURE_CONTEXT_M = 120;
const METRES_PER_DEGREE_LAT = 111_320;

/**
 * A single framed stand reads in place among its neighbours, not filling the
 * free area edge to edge: each side grows by the stand's own span.
 */
function withNeighbourContext(bounds: LatLngBounds): LatLngBounds {
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const west = bounds.getWest();
  const east = bounds.getEast();
  const metresPerDegreeLng =
    METRES_PER_DEGREE_LAT * Math.cos((((south + north) / 2) * Math.PI) / 180);
  const growLat = Math.max(north - south, LONE_FEATURE_CONTEXT_M / METRES_PER_DEGREE_LAT);
  const growLng = Math.max(east - west, LONE_FEATURE_CONTEXT_M / metresPerDegreeLng);
  return new LatLngBounds([south - growLat, west - growLng], [north + growLat, east + growLng]);
}

/** Bounds of the named features, or of the property bbox when none is named. */
export function resolveMapFitBounds(
  bbox: readonly [number, number, number, number] | null | undefined,
  geojson: GeoJSONTypes.FeatureCollection | null | undefined,
  featureIds: readonly string[] | undefined,
): LatLngBounds | null {
  if (featureIds?.length && geojson) {
    const wanted = new Set(featureIds);
    const features = geojson.features.filter((feature) => wanted.has(featureStandId(feature)));
    if (features.length) {
      const bounds = geoJSON(features).getBounds();
      if (bounds.isValid()) return features.length === 1 ? withNeighbourContext(bounds) : bounds;
    }
  }
  if (!bbox) return null;
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return new LatLngBounds([minLat, minLng], [maxLat, maxLng]);
}

/** Frame the target in the map's free area. */
export function applyMapFit(map: LeafletMap, target: MapFitTarget): void {
  const container = map.getContainer();
  if (container.clientWidth === 0 || container.clientHeight === 0) return;
  map.invalidateSize(false);
  const padding = mapFitPadding(container);
  map.fitBounds(target.bounds, {
    paddingTopLeft: [padding.left, padding.top],
    paddingBottomRight: [padding.right, padding.bottom],
    maxZoom: target.maxZoom ?? clampFitZoom(map),
    animate: false,
  });
}
